export type BlockFieldType =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'number'
  | 'select'
  | 'radio'
  | 'media'
  | 'link'
  | 'size'
  | 'overlay'
  | 'titlecard'
  | 'collection-key'
  | 'array';

export interface BlockFieldOption {
  value: string;
  label: string;
}

// Rol visual de un campo `media`. Guía el prompt de generación y, en el futuro,
// la selección de imágenes durante el onboarding.
export type MediaRole =
  | 'hero-background'
  | 'section-photo'
  | 'editorial-support'
  | 'gallery'
  | 'logo'
  | 'avatar'
  | 'screenshot';

// Política de autogeneración de imagen por campo:
//  - 'auto':   elegible para generación automática (onboarding) y bajo demanda (editor).
//  - 'manual': solo generación bajo demanda en el editor; nunca automática.
//  - 'off':    sin generación de IA (contenido factual o verificable: fotos reales,
//              capturas, logos de terceros). Es el comportamiento por defecto.
export type GenerationPolicy = 'auto' | 'manual' | 'off';

export interface BlockFieldDef {
  key: string;
  type: BlockFieldType;
  label: string;
  options?: BlockFieldOption[];
  folder?: string;
  multiple?: boolean;
  aiText?: boolean;
  arrayFields?: BlockFieldDef[];
  // Metadata de imagen (solo campos `media`). Define elegibilidad y dirección
  // creativa para la generación con IA. Sin `generationPolicy` → 'off'.
  mediaRole?: MediaRole;
  aspectRatio?: string;        // '16:9', '4:3', '1:1', '3:4', etc.
  generationPolicy?: GenerationPolicy;
  promptHints?: string;        // dirección creativa base que se pliega en el prompt
}

export interface BlockDef {
  type: string;
  label: string;
  category: 'estructura' | 'contenido' | 'colección' | 'conversión' | 'social';
  icon: string;
  fields: BlockFieldDef[];
  shell: Array<'bg' | 'pad' | 'width' | 'align' | 'anim'>;
  sizes?: Record<string, string>;
  aiBuildable: boolean;
  defaults: Record<string, unknown>;
  summaryField?: string;
}

export const SHELL_AXES = {
  bg: ['default', 'surface', 'alt', 'deep', 'deep-alt', 'image', 'custom'] as const,
  pad: ['none', 'compact', 'regular', 'spacious'] as const,
  width: ['contained', 'wide', 'full'] as const,
  align: ['left', 'center', 'right'] as const,
  anim: ['none', 'reveal', 'fade', 'rise'] as const,
  animSpeed: ['slow', 'normal', 'fast'] as const,
} as const;

export const BLOCK_CATALOG: BlockDef[] = [
  {
    type: 'AppointmentBooking',
    label: 'Reservas / Citas',
    category: 'conversión',
    icon: 'CalendarDays',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      { key: 'successTitle', type: 'text', label: 'Título de confirmación' },
      { key: 'successText', type: 'textarea', label: 'Texto de confirmación' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    aiBuildable: false,
    defaults: {
      eyebrow: 'Reserva online',
      title: 'Elige el mejor momento para ti',
      subtitle: 'Selecciona un servicio, profesional y horario.',
      successTitle: 'Reserva recibida',
      successText: 'Hemos enviado los detalles a tu correo.',
    },
    summaryField: 'title',
  },
  {
    type: 'Hero',
    label: 'Hero',
    category: 'estructura',
    icon: 'LayoutTemplate',
    fields: [
      {
        key: 'heroStyle', type: 'select', label: 'Estilo',
        options: [
          { value: 'fullscreen', label: 'Pantalla completa' },
          { value: 'split', label: 'Dividido' },
          { value: 'minimal', label: 'Minimal' },
          { value: 'editorial', label: 'Editorial' },
          { value: 'marquee', label: 'Marquee' },
          { value: 'app-showcase', label: 'App / Producto (mockups)' },
        ],
      },
      {
        key: 'backgroundImage', type: 'media', label: 'Imagen de fondo', folder: 'pages',
        mediaRole: 'hero-background', aspectRatio: '16:9', generationPolicy: 'auto',
        promptHints: 'Fondo atmosférico y editorial para la sección principal de un sitio web. ' +
          'Escena ambiental, sin texto, sin logos y sin personas reconocibles, con espacio negativo ' +
          'para superponer un titular.',
      },
      { key: 'overlay', type: 'overlay', label: 'Oscurecer la imagen de fondo' },
      { key: 'titleCard', type: 'titlecard', label: 'Recuadro detrás del titular' },
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio', aiText: true },
      { key: 'script', type: 'text', label: 'Texto decorativo (cursiva)' },
      { key: 'headline', type: 'text', label: 'Titular principal', aiText: true },
      { key: 'headlineSize', type: 'size', label: 'Tamaño del titular' },
      { key: 'headlineLight', type: 'text', label: 'Titular — parte ligera' },
      { key: 'subheadline', type: 'text', label: 'Subtítulo', aiText: true },
      { key: 'subheadlineSize', type: 'size', label: 'Tamaño del subtítulo' },
      { key: 'ctaLabel', type: 'text', label: 'Botón principal — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón principal — enlace' },
      { key: 'ctaSecondaryLabel', type: 'text', label: 'Botón secundario — texto' },
      { key: 'ctaSecondaryHref', type: 'link', label: 'Botón secundario — enlace' },
      {
        key: 'stats', type: 'array', label: 'Estadísticas (pantalla completa / App)',
        arrayFields: [
          { key: 'value', type: 'text', label: 'Valor' },
          { key: 'label', type: 'text', label: 'Descripción' },
        ],
      },
      { key: 'desktopImage', type: 'media', label: 'Captura escritorio (estilo App)', folder: 'pages', mediaRole: 'screenshot', generationPolicy: 'off' },
      { key: 'mobileImage', type: 'media', label: 'Captura móvil (estilo App)', folder: 'pages', mediaRole: 'screenshot', generationPolicy: 'off' },
      { key: 'mockupDomain', type: 'text', label: 'Dominio en el navegador (estilo App)' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { headlineSize: 'var(--fs-display-lg)', subheadlineSize: 'var(--fs-body-lg)' },
    aiBuildable: true,
    defaults: { heroStyle: 'fullscreen', headline: 'Tu titular aquí', overlay: 55, headlineSize: 'md', subheadlineSize: 'md' },
  },
  {
    type: 'PropertiesGrid',
    label: 'Propiedades',
    category: 'colección',
    icon: 'Building2',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'title1', type: 'text', label: 'Título — parte 1' },
      { key: 'title2', type: 'text', label: 'Título — parte ligera' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'badge', type: 'text', label: 'Etiqueta destacada' },
      { key: 'subtitle', type: 'text', label: 'Subtítulo' },
      { key: 'limit', type: 'number', label: 'Número máximo de propiedades' },
      {
        key: 'featured', type: 'select', label: 'Solo propiedades destacadas',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'status', type: 'select', label: 'Filtrar por estado',
        options: [
          { value: '', label: 'Todos' },
          { value: 'available', label: 'Disponible' },
          { value: 'sold', label: 'Vendido' },
          { value: 'rented', label: 'Rentado' },
        ],
      },
      {
        key: 'featuredLayout', type: 'select', label: 'Distribución',
        options: [
          { value: 'grid', label: 'Cuadrícula' },
          { value: 'bento', label: 'Bento' },
          { value: 'spread', label: 'Extendido' },
        ],
      },
      {
        key: 'columns', type: 'select', label: 'Columnas (solo cuadrícula)',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }],
      },
      {
        key: 'showCta', type: 'select', label: 'Mostrar botón',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'showFilters', type: 'select', label: 'Mostrar filtros',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    aiBuildable: false,
    defaults: { featuredLayout: 'grid', columns: '3', limit: 100 },
  },
  {
    type: 'Agents',
    label: 'Agentes',
    category: 'colección',
    icon: 'Users',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'subtitle', type: 'text', label: 'Subtítulo' },
      {
        key: 'featured', type: 'select', label: 'Solo agentes destacados',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'columns', type: 'select', label: 'Columnas',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }],
      },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { title: 'Nuestro equipo', columns: '3', titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'TextSection',
    label: 'Sección de texto',
    category: 'contenido',
    icon: 'AlignLeft',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'body', type: 'richtext', label: 'Cuerpo' },
      { key: 'body2', type: 'richtext', label: 'Segundo párrafo' },
      {
        key: 'image', type: 'media', label: 'Imagen', folder: 'pages',
        mediaRole: 'editorial-support', aspectRatio: '4:3', generationPolicy: 'auto',
        promptHints: 'Imagen editorial de apoyo para una sección de texto. Composición limpia, ' +
          'ilustrativa y coherente con el tono del negocio, sin texto superpuesto.',
      },
      { key: 'imageAlt', type: 'text', label: 'Descripción de la imagen (accesibilidad)' },
      {
        key: 'imagePosition', type: 'select', label: 'Posición de la imagen',
        options: [{ value: 'right', label: 'Derecha' }, { value: 'left', label: 'Izquierda' }],
      },
      { key: 'badgeValue', type: 'text', label: 'Etiqueta — número o valor' },
      { key: 'badgeLabel', type: 'text', label: 'Etiqueta — descripción' },
      { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: true,
    defaults: { imagePosition: 'right', body: '', titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'Stats',
    label: 'Estadísticas / Proceso',
    category: 'contenido',
    icon: 'BarChart3',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'title1', type: 'text', label: 'Título — parte 1' },
      { key: 'title2', type: 'text', label: 'Título — parte ligera' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      {
        key: 'variant', type: 'select', label: 'Variante',
        options: [{ value: 'process', label: 'Proceso' }, { value: 'stats', label: 'Estadísticas' }],
      },
      {
        key: 'steps', type: 'array', label: 'Pasos (variante Proceso)',
        arrayFields: [
          { key: 'num', type: 'text', label: 'Número' },
          { key: 'title', type: 'text', label: 'Título' },
          { key: 'description', type: 'text', label: 'Descripción' },
        ],
      },
      {
        key: 'stats', type: 'array', label: 'Estadísticas (variante Estadísticas)',
        arrayFields: [
          { key: 'value', type: 'text', label: 'Valor' },
          { key: 'label', type: 'text', label: 'Descripción' },
          { key: 'icon', type: 'text', label: 'Nombre del ícono (ej: Star, Phone, Home)' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: true,
    defaults: { variant: 'process', titleSize: 'md' },
  },
  {
    type: 'Contact',
    label: 'Contacto',
    category: 'conversión',
    icon: 'Mail',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'text', label: 'Subtítulo' },
      {
        key: 'showForm', type: 'select', label: 'Mostrar formulario',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'showMap', type: 'select', label: 'Mostrar mapa',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      { key: 'mapEmbedUrl', type: 'text', label: 'Enlace para incrustar el mapa' },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'clamp(2.65rem, 7.8vw, 5.35rem)' },
    aiBuildable: true,
    defaults: { showForm: 'true', showMap: 'false', titleSize: 'md' },
  },
  {
    type: 'CTA',
    label: 'Llamada a la acción',
    category: 'conversión',
    icon: 'Megaphone',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'headline', type: 'text', label: 'Titular' },
      { key: 'headlineSize', type: 'size', label: 'Tamaño del titular' },
      { key: 'headlineScript', type: 'text', label: 'Titular decorativo (cursiva)' },
      { key: 'body', type: 'richtext', label: 'Cuerpo' },
      { key: 'ctaLabel', type: 'text', label: 'Botón principal — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón principal — enlace' },
      { key: 'ctaSecondaryLabel', type: 'text', label: 'Botón secundario — texto' },
      { key: 'ctaSecondaryHref', type: 'link', label: 'Botón secundario — enlace' },
      {
        key: 'showContact', type: 'select', label: 'Mostrar información de contacto',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { headlineSize: 'var(--fs-heading-xl)' },
    aiBuildable: true,
    defaults: { showContact: 'true', headlineSize: 'md' },
    summaryField: 'headline',
  },
  {
    type: 'Blog',
    label: 'Blog',
    category: 'colección',
    icon: 'BookOpen',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'limit', type: 'number', label: 'Número de artículos a mostrar' },
      {
        key: 'columns', type: 'select', label: 'Columnas',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }],
      },
      {
        key: 'showCta', type: 'select', label: 'Mostrar botón',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { limit: 3, columns: '3', showCta: 'true', ctaHref: '/blog', titleSize: 'md' },
  },
  {
    type: 'Testimonials',
    label: 'Testimonios',
    category: 'social',
    icon: 'Quote',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'headline', type: 'text', label: 'Titular' },
      { key: 'headlineScript', type: 'text', label: 'Titular decorativo (cursiva)' },
      {
        key: 'source', type: 'radio', label: 'Origen',
        options: [
          { value: 'manual', label: 'Escribir manualmente' },
          { value: 'approved', label: 'Usar testimonios aprobados' },
        ],
      },
      { key: 'headlineSize', type: 'size', label: 'Tamaño del titular' },
      { key: 'limit', type: 'number', label: 'Máximo a mostrar' },
      {
        key: 'testimonials', type: 'array', label: 'Testimonios',
        arrayFields: [
          { key: 'quote', type: 'richtext', label: 'Cita' },
          { key: 'author', type: 'text', label: 'Autor' },
          { key: 'location', type: 'text', label: 'Ubicación' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { headlineSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { source: 'manual', limit: 6, testimonials: [], headlineSize: 'md' },
    summaryField: 'headline',
  },
  {
    type: 'StickyScroll',
    label: 'Scroll narrativo',
    category: 'contenido',
    icon: 'Columns',
    fields: [
      { key: 'ruleLabel', type: 'text', label: 'Etiqueta izquierda' },
      { key: 'ruleRight', type: 'text', label: 'Etiqueta derecha' },
      {
        key: 'image', type: 'media', label: 'Imagen', folder: 'pages',
        mediaRole: 'editorial-support', aspectRatio: '3:4', generationPolicy: 'auto',
        promptHints: 'Imagen vertical de apoyo para un scroll narrativo. Atmósfera coherente con ' +
          'el relato, ilustrativa y sin texto.',
      },
      { key: 'imageAlt', type: 'text', label: 'Descripción de la imagen (accesibilidad)' },
      { key: 'imageCaption', type: 'text', label: 'Pie de imagen' },
      {
        key: 'steps', type: 'array', label: 'Pasos',
        arrayFields: [
          { key: 'num', type: 'text', label: 'Número' },
          { key: 'title', type: 'text', label: 'Título' },
          { key: 'description', type: 'text', label: 'Descripción' },
        ],
      },
      { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
    ],
    shell: ['bg', 'pad', 'anim'],
    aiBuildable: false,
    defaults: { steps: [] },
  },
  {
    type: 'FAQ',
    label: 'Preguntas frecuentes',
    category: 'contenido',
    icon: 'HelpCircle',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      {
        key: 'items', type: 'array', label: 'Preguntas',
        arrayFields: [
          { key: 'question', type: 'text', label: 'Pregunta' },
          { key: 'answer', type: 'textarea', label: 'Respuesta' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-md)' },
    aiBuildable: true,
    defaults: {
      eyebrow: 'Preguntas frecuentes',
      title: 'Todo lo que necesitas saber',
      items: [],
      titleSize: 'md',
    },
    summaryField: 'title',
  },
  {
    type: 'Offices',
    label: 'Sucursales',
    category: 'colección',
    icon: 'MapPin',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'subtitle', type: 'text', label: 'Subtítulo' },
      {
        key: 'showMap', type: 'radio', label: 'Mostrar mapa',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'columns', type: 'radio', label: 'Columnas',
        options: [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }],
      },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: {
      eyebrow: 'Dónde encontrarnos',
      title: 'Nuestras sucursales',
      showMap: 'true',
      columns: '2',
      titleSize: 'md',
    },
    summaryField: 'title',
  },
  {
    type: 'GoogleReviews',
    label: 'Reseñas de Google',
    category: 'social',
    icon: 'Star',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      {
        key: 'showReviews', type: 'radio', label: 'Mostrar las reseñas',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'Solo la calificación' }],
      },
      { key: 'ctaLabel', type: 'text', label: 'Texto del botón para dejar reseña' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    aiBuildable: false,
    defaults: {
      eyebrow: 'Opiniones de clientes',
      title: 'Lo que dicen en',
      titleScript: 'Google',
      showReviews: 'true',
    },
    summaryField: 'title',
  },
  {
    type: 'LinkInBio',
    label: 'Página de enlaces',
    category: 'social',
    icon: 'Link',
    fields: [
      { key: 'avatar', type: 'media', label: 'Foto o logo', folder: 'pages', mediaRole: 'avatar', aspectRatio: '1:1', generationPolicy: 'off' },
      { key: 'name', type: 'text', label: 'Nombre' },
      { key: 'nameSize', type: 'size', label: 'Tamaño del nombre' },
      { key: 'tagline', type: 'textarea', label: 'Descripción corta' },
      {
        key: 'links', type: 'array', label: 'Botones',
        arrayFields: [
          { key: 'label', type: 'text', label: 'Texto del botón' },
          {
            key: 'type', type: 'select', label: 'Tipo',
            options: [
              { value: 'link', label: 'Enlace' },
              { value: 'whatsapp', label: 'WhatsApp' },
              { value: 'menu', label: 'Menú' },
              { value: 'maps', label: 'Cómo llegar' },
              { value: 'phone', label: 'Teléfono' },
              { value: 'instagram', label: 'Instagram' },
              { value: 'evento', label: 'Eventos / reservas' },
            ],
          },
          { key: 'url', type: 'link', label: 'Enlace (o número para WhatsApp/teléfono)' },
          {
            key: 'featured', type: 'select', label: 'Destacar',
            options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
          },
          {
            key: 'hidden', type: 'select', label: 'Mostrar',
            options: [{ value: 'false', label: 'Sí' }, { value: 'true', label: 'Ocultar' }],
          },
        ],
      },
      {
        key: 'showSocial', type: 'select', label: 'Mostrar mis redes sociales',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'hideChrome', type: 'select', label: 'Versión minimalista (sin menú ni pie)',
        options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
      },
    ],
    shell: ['bg'],
    sizes: { nameSize: 'var(--fs-display-sm)' },
    aiBuildable: false,
    defaults: { name: '', tagline: '', links: [], showSocial: 'true', hideChrome: 'false', nameSize: 'md' },
  },
  {
    type: 'InstagramHighlights',
    label: 'Destacados de Instagram',
    category: 'social',
    icon: 'Image',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      {
        key: 'items', type: 'array', label: 'Publicaciones',
        arrayFields: [
          { key: 'image', type: 'media', label: 'Imagen', folder: 'pages', mediaRole: 'gallery', aspectRatio: '1:1', generationPolicy: 'off' },
          { key: 'url', type: 'link', label: 'Enlace a la publicación' },
          { key: 'caption', type: 'text', label: 'Descripción (accesibilidad)' },
        ],
      },
      {
        key: 'columns', type: 'select', label: 'Columnas',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }],
      },
      { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón — enlace a tu Instagram' },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    aiBuildable: false,
    defaults: { items: [], columns: '3', ctaLabel: 'Síguenos en Instagram' },
  },
  {
    type: 'CollectionSection',
    label: 'Sección de contenido',
    category: 'colección',
    icon: 'Grid3x3',
    fields: [
      { key: 'collectionKey', type: 'collection-key', label: 'Qué mostrar' },
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio', aiText: true },
      { key: 'title', type: 'text', label: 'Título', aiText: true },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'subtitle', type: 'text', label: 'Subtítulo', aiText: true },
      {
        key: 'columns', type: 'select', label: 'Columnas',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }],
      },
      { key: 'limit', type: 'number', label: 'Número máximo de elementos' },
      {
        key: 'featured', type: 'select', label: 'Solo destacados',
        options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
      },
      {
        key: 'showCta', type: 'select', label: 'Mostrar botón',
        options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
      },
      { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
      { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    aiBuildable: true,
    defaults: { columns: '3', featured: 'false', showCta: 'false' },
  },
  {
    type: 'Gallery',
    label: 'Galería de imágenes',
    category: 'contenido',
    icon: 'Images',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      {
        key: 'layout', type: 'select', label: 'Distribución',
        options: [
          { value: 'grid', label: 'Cuadrícula' },
          { value: 'masonry', label: 'Mosaico' },
          { value: 'carousel', label: 'Carrusel' },
        ],
      },
      {
        key: 'columns', type: 'select', label: 'Columnas (cuadrícula y mosaico)',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }],
      },
      {
        key: 'items', type: 'array', label: 'Imágenes',
        arrayFields: [
          {
            key: 'image', type: 'media', label: 'Imagen', folder: 'pages',
            mediaRole: 'gallery', aspectRatio: '4:3', generationPolicy: 'manual',
            promptHints: 'Imagen ilustrativa para una galería visual. Composición estética y ' +
              'coherente con el negocio, sin texto superpuesto.',
          },
          { key: 'alt', type: 'text', label: 'Descripción (accesibilidad)' },
          { key: 'caption', type: 'text', label: 'Pie de foto' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { layout: 'grid', columns: '3', items: [], titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'LogoStrip',
    label: 'Logos / Confían en nosotros',
    category: 'social',
    icon: 'Award',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'grayscale', type: 'select', label: 'Mostrar logos en gris',
        options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }],
      },
      {
        key: 'items', type: 'array', label: 'Logos',
        arrayFields: [
          { key: 'image', type: 'media', label: 'Logo', folder: 'pages', mediaRole: 'logo', generationPolicy: 'off' },
          { key: 'alt', type: 'text', label: 'Nombre (accesibilidad)' },
          { key: 'url', type: 'link', label: 'Enlace (opcional)' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    aiBuildable: false,
    defaults: { grayscale: 'true', items: [] },
    summaryField: 'title',
  },
  {
    type: 'Pricing',
    label: 'Precios / Planes',
    category: 'conversión',
    icon: 'Tags',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      {
        key: 'plans', type: 'array', label: 'Planes',
        arrayFields: [
          { key: 'name', type: 'text', label: 'Nombre del plan' },
          { key: 'price', type: 'text', label: 'Precio' },
          { key: 'period', type: 'text', label: 'Periodo (ej: /mes)' },
          { key: 'description', type: 'text', label: 'Descripción breve' },
          {
            key: 'features', type: 'array', label: 'Incluye',
            arrayFields: [{ key: 'text', type: 'text', label: 'Característica' }],
          },
          {
            key: 'featured', type: 'select', label: 'Plan destacado',
            options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
          },
          { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
          { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { plans: [], titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'Features',
    label: 'Servicios / Ventajas',
    category: 'contenido',
    icon: 'LayoutGrid',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      {
        key: 'columns', type: 'select', label: 'Columnas',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }],
      },
      {
        key: 'items', type: 'array', label: 'Elementos',
        arrayFields: [
          {
            key: 'icon', type: 'select', label: 'Ícono',
            options: [
              { value: 'Star', label: 'Estrella' },
              { value: 'Heart', label: 'Corazón' },
              { value: 'Shield', label: 'Escudo' },
              { value: 'Award', label: 'Premio' },
              { value: 'Zap', label: 'Rayo' },
              { value: 'Clock', label: 'Reloj' },
              { value: 'Check', label: 'Palomita' },
              { value: 'Truck', label: 'Envío' },
              { value: 'Gift', label: 'Regalo' },
              { value: 'Users', label: 'Personas' },
              { value: 'ThumbsUp', label: 'Aprobación' },
              { value: 'Sparkles', label: 'Destellos' },
              { value: 'Leaf', label: 'Hoja' },
              { value: 'Home', label: 'Casa' },
              { value: 'Phone', label: 'Teléfono' },
              { value: 'MapPin', label: 'Ubicación' },
              { value: 'Calendar', label: 'Calendario' },
              { value: 'CreditCard', label: 'Tarjeta' },
              { value: 'Wrench', label: 'Herramienta' },
              { value: 'Camera', label: 'Cámara' },
            ],
          },
          { key: 'title', type: 'text', label: 'Título' },
          { key: 'description', type: 'textarea', label: 'Descripción' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { columns: '3', items: [], titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'Video',
    label: 'Video',
    category: 'contenido',
    icon: 'Play',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      {
        key: 'provider', type: 'select', label: 'Plataforma',
        options: [{ value: 'youtube', label: 'YouTube' }, { value: 'vimeo', label: 'Vimeo' }],
      },
      { key: 'videoId', type: 'text', label: 'ID del video' },
      {
        key: 'poster', type: 'media', label: 'Miniatura (opcional)', folder: 'pages',
        mediaRole: 'editorial-support', aspectRatio: '16:9', generationPolicy: 'manual',
        promptHints: 'Miniatura ilustrativa para un video. Imagen atractiva y limpia que invite a ' +
          'reproducir, sin texto superpuesto.',
      },
      { key: 'caption', type: 'text', label: 'Pie de video' },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { provider: 'youtube', titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'Divider',
    label: 'Separador',
    category: 'estructura',
    icon: 'Minus',
    fields: [
      {
        key: 'variant', type: 'select', label: 'Estilo',
        options: [
          { value: 'space', label: 'Solo espacio' },
          { value: 'line', label: 'Línea' },
          { value: 'ornament', label: 'Adorno central' },
        ],
      },
      {
        key: 'height', type: 'select', label: 'Altura',
        options: [
          { value: 'sm', label: 'Pequeña' },
          { value: 'md', label: 'Mediana' },
          { value: 'lg', label: 'Grande' },
          { value: 'xl', label: 'Muy grande' },
        ],
      },
    ],
    shell: ['bg'],
    aiBuildable: false,
    defaults: { variant: 'line', height: 'md' },
  },
  {
    type: 'Search',
    label: 'Buscador',
    category: 'colección',
    icon: 'Search',
    fields: [
      { key: 'href', type: 'link', label: 'Página de resultados' },
      {
        key: 'overlap', type: 'select', label: 'Solapar con el bloque de arriba',
        options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
      },
    ],
    shell: [],
    aiBuildable: false,
    defaults: { overlap: 'false' },
  },
  {
    type: 'Checklist',
    label: 'Lista de cualificación',
    category: 'contenido',
    icon: 'ListChecks',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      {
        key: 'items', type: 'array', label: 'Elementos',
        arrayFields: [{ key: 'text', type: 'text', label: 'Texto' }],
      },
    ],
    shell: ['bg', 'pad', 'width', 'align', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: true,
    defaults: { items: [], titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'Comparison',
    label: 'Comparativa (antes / después)',
    category: 'contenido',
    icon: 'Columns2',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      { key: 'negativeTitle', type: 'text', label: 'Columna negativa — título' },
      {
        key: 'negativeItems', type: 'array', label: 'Columna negativa — elementos',
        arrayFields: [
          {
            key: 'icon', type: 'select', label: 'Ícono',
            options: [
              { value: 'Frown', label: 'Cara triste' },
              { value: 'X', label: 'Cruz' },
              { value: 'MessageSquareOff', label: 'Mensaje tachado' },
              { value: 'BellOff', label: 'Campana tachada' },
              { value: 'FileX', label: 'Archivo tachado' },
              { value: 'UserX', label: 'Usuario tachado' },
              { value: 'EyeOff', label: 'Ojo tachado' },
              { value: 'CalendarX', label: 'Calendario tachado' },
              { value: 'ClipboardX', label: 'Portapapeles tachado' },
              { value: 'AlertTriangle', label: 'Alerta' },
              { value: 'ThumbsDown', label: 'Pulgar abajo' },
            ],
          },
          { key: 'text', type: 'text', label: 'Texto' },
        ],
      },
      { key: 'positiveTitle', type: 'text', label: 'Columna positiva — título' },
      {
        key: 'positiveItems', type: 'array', label: 'Columna positiva — elementos',
        arrayFields: [{ key: 'text', type: 'text', label: 'Texto' }],
      },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: true,
    defaults: { negativeItems: [], positiveItems: [], titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'SocialLinks',
    label: 'Redes sociales',
    category: 'social',
    icon: 'Share2',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      { key: 'ctaLabel', type: 'text', label: 'Texto del botón (ej: Seguir)' },
      {
        key: 'items', type: 'array', label: 'Redes',
        arrayFields: [
          {
            key: 'network', type: 'select', label: 'Red',
            options: [
              { value: 'instagram', label: 'Instagram' },
              { value: 'facebook', label: 'Facebook' },
              { value: 'youtube', label: 'YouTube' },
              { value: 'tiktok', label: 'TikTok' },
              { value: 'twitter', label: 'X / Twitter' },
              { value: 'linkedin', label: 'LinkedIn' },
              { value: 'whatsapp', label: 'WhatsApp' },
              { value: 'link', label: 'Otro enlace' },
            ],
          },
          { key: 'label', type: 'text', label: 'Nombre visible' },
          { key: 'handle', type: 'text', label: 'Usuario / handle' },
          { key: 'url', type: 'text', label: 'Enlace (vacío = próximamente)' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: { items: [], titleSize: 'md' },
    summaryField: 'title',
  },
  {
    type: 'PricingCalculator',
    label: 'Calculadora de precios',
    category: 'conversión',
    icon: 'Calculator',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Texto introductorio' },
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'titleScript', type: 'text', label: 'Título decorativo (cursiva)' },
      { key: 'titleSize', type: 'size', label: 'Tamaño del título' },
      { key: 'subtitle', type: 'textarea', label: 'Subtítulo' },
      { key: 'calloutText', type: 'textarea', label: 'Nota destacada (HTML permitido)' },
      { key: 'calloutCtaLabel', type: 'text', label: 'Nota — botón texto' },
      { key: 'calloutCtaHref', type: 'link', label: 'Nota — botón enlace' },
      { key: 'unitLabel', type: 'text', label: 'Etiqueta del control (ej: Número de unidades)' },
      { key: 'unitSingular', type: 'text', label: 'Unidad — singular' },
      { key: 'unitPlural', type: 'text', label: 'Unidad — plural' },
      { key: 'defaultUnits', type: 'number', label: 'Unidades por defecto' },
      { key: 'customThreshold', type: 'number', label: 'Umbral de plan personalizado' },
      { key: 'annualFreeMonths', type: 'number', label: 'Meses gratis al pagar anual' },
      { key: 'currencySymbol', type: 'text', label: 'Símbolo de moneda' },
      { key: 'refFee', type: 'number', label: 'Cuota de referencia (insight coste/unidad)' },
      { key: 'refFeeLabel', type: 'text', label: 'Referencia — etiqueta de unidad (ej: casa)' },
      { key: 'customTitle', type: 'text', label: 'Plan grande — título' },
      { key: 'customText', type: 'textarea', label: 'Plan grande — texto' },
      { key: 'customCtaLabel', type: 'text', label: 'Plan grande — botón texto' },
      { key: 'customCtaHref', type: 'link', label: 'Plan grande — botón enlace' },
      {
        key: 'plans', type: 'array', label: 'Planes',
        arrayFields: [
          { key: 'name', type: 'text', label: 'Nombre' },
          { key: 'badge', type: 'text', label: 'Etiqueta (ej: Recomendado)' },
          { key: 'basePrice', type: 'number', label: 'Precio base' },
          { key: 'baseUnits', type: 'number', label: 'Unidades incluidas en el base' },
          { key: 'additionalUnitPrice', type: 'number', label: 'Precio por unidad adicional' },
          { key: 'description', type: 'text', label: 'Descripción' },
          {
            key: 'featured', type: 'select', label: 'Plan destacado',
            options: [{ value: 'false', label: 'No' }, { value: 'true', label: 'Sí' }],
          },
          {
            key: 'features', type: 'array', label: 'Incluye',
            arrayFields: [{ key: 'text', type: 'text', label: 'Característica' }],
          },
          { key: 'ctaLabel', type: 'text', label: 'Botón — texto' },
          { key: 'ctaHref', type: 'link', label: 'Botón — enlace' },
        ],
      },
    ],
    shell: ['bg', 'pad', 'width', 'anim'],
    sizes: { titleSize: 'var(--fs-heading-lg)' },
    aiBuildable: false,
    defaults: {
      plans: [], titleSize: 'md', defaultUnits: 20, customThreshold: 100,
      annualFreeMonths: 2, currencySymbol: '$',
    },
    summaryField: 'title',
  },
];
